import {
  Component,
  ElementRef,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, takeUntil, tap } from 'rxjs';
import { LoginAuthProperties } from 'server/shared/helpers/types/AuthProperties';
import { ClientThirdPartyLoginConfig } from 'server/shared/models/customer';
import { AdfsService } from 'src/app/adfs/adfs.service';
import { MsalService } from 'src/app/msal/msal.service';
import { AccountService } from '../../services/account.service';
import { ToastService } from '../../services/toast/toast.service';
import { SubscriptionService } from '../../shared/subscription.service';

export enum LoginStepperEnum {
  EMAIL,
  PASSWORD,
}
@Component({
  selector: 'login-form',
  templateUrl: 'login-form.component.html',
  styleUrls: ['login-form.component.scss'],
})
export class LoginFormComponent implements OnInit, OnDestroy {
  @ViewChild('emailInput') emailInputElement?: ElementRef<HTMLInputElement>;
  @ViewChild('passwordInput')
  passwordInputElement?: ElementRef<HTMLInputElement>;
  destroyed$ = new Subject<void>();

  LoginStepperEnum = LoginStepperEnum;
  loginStep: LoginStepperEnum = LoginStepperEnum.EMAIL;

  loginForm = this.getLoginForm();

  showPassword = false;

  getLoginForm(emailValue?: string | null, passwordValue?: string | null) {
    return new FormGroup(
      {
        email: new FormControl(emailValue, [
          Validators.required,
          Validators.email,
        ]),
        password: new FormControl(passwordValue, [Validators.required]),
      },
      { updateOn: 'submit' },
    );
  }
  authData?: LoginAuthProperties;

  loginWithMsalEffect$ = this.msalService.tokens$.pipe(
    takeUntil(this.destroyed$),
    tap((tokens) => {
      this.accountService.loginWithMsal(
        tokens,
        this.accountService.getStorageLoginEmail() || '',
      );
    }),
  );

  loginTracker?: Tracker.Computation;

  isLoadingAdfsLogin = false;

  loadingCancelled = false;

  msalLoading$ = this.msalService.loading$;

  constructor(
    private router: Router,
    private toastService: ToastService,
    private ngZone: NgZone,
    private subscriptionService: SubscriptionService,
    private route: ActivatedRoute,
    public accountService: AccountService,
    private msalService: MsalService,
    private adfsService: AdfsService,
  ) {
    this.loginWithMsalEffect$.subscribe();

    const loginEmail = this.accountService.getStorageLoginEmail();
    if (loginEmail) {
      this.getLoginConfigurations(loginEmail).then((configs) => {
        this.setLoginConfigs(configs);
        const code = this.route.snapshot.queryParams['code'];
        const state = this.route.snapshot.queryParams['state'];
        const error = this.route.snapshot.queryParams['error'];
        if (configs.adfsConfig && code && state && !error) {
          this.isLoadingAdfsLogin = true;
          this.accountService.loginWithAdfs(
            code,
            this.accountService.getStorageLoginEmail() || '',
          );
        }
      });
    }
  }

  ngOnInit() {
    const storedEmail = this.accountService.getStorageLoginEmail();
    this.loginForm.controls.email.setValue(storedEmail);
    this.loginTracker = Tracker.autorun((computation) => {
      if (Meteor.userId()) {
        Meteor.apply('getIsInSetup', [], {
          wait: false,
          onResultReceived: (error, result: any) => {
            this.ngZone.run(() => {
              if (
                result &&
                result.isInSetup &&
                result.phoneConfirmed &&
                result.emailConfirmed
              ) {
                this.subscriptionService.subscribe();
                this.router.navigate(['/profile']).then();
              } else if (
                result &&
                result.isInSetup &&
                (!result.phoneConfirmed || !result.emailConfirmed)
              ) {
                this.router.navigate(['/welcome']).then();
              } else {
                if (this.route.snapshot.queryParams['return']) {
                  this.router
                    .navigateByUrl(this.route.snapshot.queryParams['return'])
                    .then();
                } else {
                  window.location.href = window.location.origin + '/dashboard';
                }
              }
            });
          },
        });
        computation.stop();
      }
    });

    const errorParam = this.route.snapshot.queryParams['error'];
    const stateParam = this.route.snapshot.queryParams['state'];
    const codeParam = this.route.snapshot.queryParams['code'];
    // Auto-submit email step if stored email is present and valid
    if (
      storedEmail &&
      this.loginForm.controls.email.valid &&
      !errorParam &&
      !stateParam &&
      !codeParam
    ) {
      this.submitLoginStep(false);
    }
  }

  setLoginConfigs(configs: ClientThirdPartyLoginConfig) {
    if (configs.msalConfig) {
      this.msalService.setMsalConfig(configs.msalConfig);
      return;
    }
    if (configs.adfsConfig) {
      this.adfsService.setAdfsConfig(configs.adfsConfig);
    }
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
    if (this.loginTracker) {
      this.loginTracker.stop();
    }
  }

  isLoggedIn(): boolean {
    return !!Meteor.userId();
  }

  goToEmailStep() {
    this.loginStep = LoginStepperEnum.EMAIL;
    setTimeout(() => {
      this.emailInputElement?.nativeElement.focus();
    });
  }

  async getLoginConfigurations(
    email: string,
  ): Promise<ClientThirdPartyLoginConfig> {
    return new Promise((resolve, reject) => {
      Meteor.call(
        'getLoginConfigurations',
        email,
        (error: any, configs: any) => {
          if (error) {
            reject(error);
          }
          resolve(configs);
        },
      );
    });
  }
  passwordSubmitted = false;
  async submitLoginStep(markSubmitted = true) {
    // markSubmitted is to reflect the behavior of the mat-form-field displaying the required error
    if (this.loginStep === LoginStepperEnum.EMAIL) {
      if (this.loginForm?.controls?.email.invalid) return;
      const email = this.loginForm?.controls?.email.value;
      if (!email) return;
      this.accountService.setStorageLoginEmail(email);
      // Check for login configurations (MSAL and ADFS)
      const configs = await this.getLoginConfigurations(email);
      this.setLoginConfigs(configs);

      this.ngZone.run(() => {
        if (configs.msalConfig) {
          this.msalService.login();
          return;
        }
        if (configs.adfsConfig) {
          this.adfsService.login();
          return;
        }
        // No third-party login configured, proceed to password step
        this.loginStep = LoginStepperEnum.PASSWORD;
        // Focus password input when password step is shown, but only if it's not autofilled
        setTimeout(() => {
          this.passwordInputElement?.nativeElement.focus();
        });
        // if we already have the password (usually from autofill), submit it immediately
      });
      // has submitted email step, so we break here.
      return;
    }
    this.passwordSubmitted = false;
    if (this.loginForm?.invalid) return;
    this.authData = {
      user: { email: this.loginForm?.controls.email.value },
      password: this.loginForm?.controls.password.value,
    };
    this.accountService.loginMethod(this.authData);
  }

  logout() {
    this.msalService.logout();
    // this.adfsService.logout();
  }
}
